/**
 * コラムアーカイブ: カテゴリは URL を変えず REST で一覧を差し替え（ページ全体の再読み込みなし）
 * 件数: PC 9 / SP 8
 */
const COLUMN_CARD_ARROW_SVG =
  '<svg viewBox="0 0 57 7" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0.800781 5.7998H54.8008L49.8008 0.799805" stroke="white" stroke-width="1.6" stroke-linecap="round"/></svg>';

class ColumnArchive {
  constructor() {
    this.root = document.querySelector('.js-column-archive');
    if (!this.root || typeof sanokikoColumnArchive === 'undefined') {
      return;
    }

    this._reqId = 0;
    this._activeTermId = null;
    this._currentCategoryPage = 1;
    this._currentCategoryMaxPages = 1;

    this.pcPerPage = sanokikoColumnArchive.postsPerPagePc || 9;
    this.spPerPage = sanokikoColumnArchive.postsPerPageSp || 8;
    this.fallbackImage = sanokikoColumnArchive.fallbackImage || '';

    this.listEl = this.root.querySelector('.js-column-archive-list');
    this.anchorEl = document.getElementById('archive-anchor');
    this.pagingWrap = this.root.querySelector('.js-column-archive-paging');
    this.filters = this.root.querySelectorAll('.js-column-cat-filter');
    this.restUrl = sanokikoColumnArchive.restUrl;
    this.termNames = sanokikoColumnArchive.termNames || {};
    this.emptyMessage =
      sanokikoColumnArchive.emptyMessage || '該当するコラムはありません。';

    if (!this.listEl || !this.filters.length) {
      return;
    }

    this.initialListHtml = this.listEl.innerHTML;
    this.initialPagingHtml = this.pagingWrap ? this.pagingWrap.innerHTML : '';

    this._loadingOverlay = this._createLoadingOverlay();
    this.root.style.position = 'relative';
    this.root.appendChild(this._loadingOverlay);

    this._bind();
    this._bindPagingDelegation();
    this._bindViewportChange();
    this._applyUrlCatParam();
  }

  /**
   * 別ページから /column/?cat=slug で遷移した場合、該当カテゴリで絞り込む
   * （事例アーカイブ /works/?cat= と同仕様）
   */
  _applyUrlCatParam() {
    const catParam = new URLSearchParams(window.location.search).get('cat');
    if (!catParam) return;

    const slug = catParam.split(',')[0].trim();
    if (!slug) return;

    const btn = [...this.filters].find((b) => b.dataset.termSlug === slug);
    if (!btn) return;

    const termId = btn.dataset.termId;
    if (!termId) return;

    this._activateFilter(btn);
    this._loadCategory(termId, 1);
  }

  _createLoadingOverlay() {
    const overlay = document.createElement('div');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('role', 'status');
    Object.assign(overlay.style, {
      position: 'absolute',
      inset: '0',
      zIndex: '10',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(255,255,255,0.75)',
      opacity: '0',
      pointerEvents: 'none',
      transition: 'opacity 0.2s',
    });

    const spinner = document.createElement('span');
    Object.assign(spinner.style, {
      display: 'block',
      width: '4rem',
      height: '4rem',
      borderRadius: '50%',
      border: '.4rem solid #e0e0e0',
      borderTopColor: '#2A892C',
      animation: 'column-archive-spin 0.8s linear infinite',
    });
    overlay.appendChild(spinner);

    if (!document.getElementById('column-archive-spin-style')) {
      const style = document.createElement('style');
      style.id = 'column-archive-spin-style';
      style.textContent =
        '@keyframes column-archive-spin{to{transform:rotate(360deg)}}';
      document.head.appendChild(style);
    }

    return overlay;
  }

  _showLoading() {
    this._loadingOverlay.style.opacity = '1';
    this._loadingOverlay.style.pointerEvents = 'auto';
  }

  _hideLoading() {
    this._loadingOverlay.style.opacity = '0';
    this._loadingOverlay.style.pointerEvents = 'none';
  }

  _getPerPage() {
    return window.matchMedia('(max-width: 767px)').matches
      ? this.spPerPage
      : this.pcPerPage;
  }

  _scrollToArchiveAnchor() {
    const el = this.anchorEl || this.listEl;
    if (!el) return;
    const prefersReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    requestAnimationFrame(() => {
      el.scrollIntoView({
        behavior: prefersReduced ? 'auto' : 'smooth',
        block: 'start',
      });
    });
  }

  _bind() {
    this.filters.forEach((btn) => {
      btn.addEventListener('click', () => {
        const termId = btn.dataset.termId;
        this._activateFilter(btn);
        if (!termId) {
          this._showAll();
        } else {
          this._loadCategory(termId, 1);
        }
      });
    });
  }

  _bindPagingDelegation() {
    if (!this.pagingWrap) return;
    this.pagingWrap.addEventListener('click', (e) => {
      if (this._activeTermId == null) return;
      const pageBtn = e.target.closest('.js-column-archive-page');
      if (pageBtn) {
        const p = parseInt(pageBtn.dataset.page, 10);
        if (!Number.isNaN(p)) {
          this._loadCategory(this._activeTermId, p);
        }
        return;
      }
      if (e.target.closest('.js-column-archive-prev')) {
        if (this._currentCategoryPage > 1) {
          this._loadCategory(this._activeTermId, this._currentCategoryPage - 1);
        }
        return;
      }
      if (e.target.closest('.js-column-archive-next')) {
        if (this._currentCategoryPage < this._currentCategoryMaxPages) {
          this._loadCategory(this._activeTermId, this._currentCategoryPage + 1);
        }
      }
    });
  }

  _bindViewportChange() {
    let prev = this._getPerPage();
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = () => {
      const next = this._getPerPage();
      if (next === prev) return;
      prev = next;
      if (this._activeTermId != null) {
        this._loadCategory(this._activeTermId, 1);
      }
    };
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
    } else {
      mq.addListener(onChange);
    }
  }

  _activateFilter(activeBtn) {
    this.filters.forEach((b) => {
      const on = b === activeBtn;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (on) {
        b.setAttribute('aria-current', 'true');
      } else {
        b.removeAttribute('aria-current');
      }
    });
  }

  _showAll() {
    this._activeTermId = null;
    this._reqId += 1;
    this.listEl.innerHTML = this.initialListHtml;
    if (this.pagingWrap) {
      this.pagingWrap.innerHTML = this.initialPagingHtml;
      this.pagingWrap.hidden = false;
    }
  }

  _clearPaging() {
    if (this.pagingWrap) {
      this.pagingWrap.innerHTML = '';
      this.pagingWrap.hidden = true;
    }
  }

  _setEmpty() {
    this.listEl.innerHTML = `<li class="text-center text-[1.4rem] text-[#888888] py-[2rem]">${this.emptyMessage}</li>`;
    this._clearPaging();
    this._scrollToArchiveAnchor();
  }

  async _loadCategory(termId, page = 1) {
    const req = ++this._reqId;
    this._activeTermId = termId;
    this._showLoading();

    let result;
    try {
      result = await this._fetchPostsPage(termId, page);
    } catch (e) {
      if (req !== this._reqId) return;
      this._hideLoading();
      this._setEmpty();
      return;
    }

    if (req !== this._reqId) return;
    this._hideLoading();

    const { posts, totalPages } = result;
    this._currentCategoryPage = page;
    this._currentCategoryMaxPages = Math.max(1, totalPages);

    if (!posts.length) {
      this._setEmpty();
      return;
    }

    this.listEl.innerHTML = posts.map((p) => this._renderPost(p)).join('');

    if (this.pagingWrap) {
      this.pagingWrap.hidden = false;
      this.pagingWrap.innerHTML = this._renderPagingNav(
        page,
        this._currentCategoryMaxPages,
      );
    }

    this._scrollToArchiveAnchor();
  }

  async _fetchPostsPage(termId, page) {
    const perPage = this._getPerPage();
    const url = new URL(this.restUrl, window.location.origin);
    url.searchParams.set('per_page', String(perPage));
    url.searchParams.set('page', String(page));
    url.searchParams.set('column_cat', String(termId));
    url.searchParams.set('_embed', '1');

    const res = await fetch(url.toString(), {
      credentials: 'same-origin',
    });

    if (!res.ok) {
      throw new Error('REST request failed');
    }

    const totalPages = parseInt(res.headers.get('X-WP-TotalPages') || '0', 10);
    const posts = await res.json();
    return {
      posts: Array.isArray(posts) ? posts : [],
      totalPages: Number.isFinite(totalPages) ? totalPages : 0,
    };
  }

  _buildPageItems(maxPages, paged) {
    if (maxPages <= 1) return [];
    const show = { 1: true, [maxPages]: true };
    const left = Math.max(2, paged - 2);
    const right = Math.min(maxPages - 1, paged + 2);
    for (let i = left; i <= right; i++) show[i] = true;
    const keys = Object.keys(show)
      .map(Number)
      .sort((a, b) => a - b);
    const items = [];
    let prevKey = 0;
    for (const p of keys) {
      if (prevKey && p - prevKey > 1) {
        items.push({ ellipsis: true });
      }
      items.push({ page: p });
      prevKey = p;
    }
    return items;
  }

  _renderPagingNav(paged, maxPages) {
    if (maxPages <= 1) return '';

    const clsNav =
      'flex justify-center items-center gap-[.6rem] [&_*]:font-outfit';
    const clsPrevNextBase =
      'w-[3.6rem] h-[3.6rem] sp:w-[3rem] sp:h-[3rem] rounded-full flex items-center justify-center font-bold text-[1.4rem] duration-300 pc:hover:opacity-70';
    const clsBtn =
      'js-column-archive-page w-[3.6rem] h-[3.6rem] sp:w-[3rem] sp:h-[3rem] rounded-full flex items-center justify-center font-normal text-white text-[1.8rem] duration-300 [background:_radial-gradient(94.38%_94.38%_at_19.25%_6.75%,_#3DA73F_0%,_#2A892C_100%)] border border-[#CCCCCC] pc:hover:opacity-70';
    const clsCurrent =
      'w-[3.6rem] h-[3.6rem] sp:w-[3rem] sp:h-[3rem] rounded-full flex items-center justify-center font-normal text-[1.8rem] duration-300 bg-white [border:_.1rem_solid_#2A892C] text-[#2A892C]';
    const clsEllipsis =
      'w-[3.6rem] h-[3.6rem] sp:w-[3rem] sp:h-[3rem] flex items-center justify-center text-[1.8rem] text-[#888888]';

    const svgPrev = `<span style="transform:scaleX(-1);display:flex;"><svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M1 1L6 6L1 11" stroke="#2A892C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg></span>`;
    const svgNext = `<svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M1 1L6 6L1 11" stroke="#2A892C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>`;

    let prevBlock;
    if (paged <= 1) {
      prevBlock = `<span class="${clsPrevNextBase} opacity-30 pointer-events-none" aria-hidden="true">${svgPrev}</span>`;
    } else {
      prevBlock = `<button type="button" class="${clsPrevNextBase} js-column-archive-prev bg-white text-[#2A892C]" aria-label="前のページへ">${svgPrev}</button>`;
    }

    let nextBlock;
    if (paged >= maxPages) {
      nextBlock = `<span class="${clsPrevNextBase} opacity-30 pointer-events-none" aria-hidden="true">${svgNext}</span>`;
    } else {
      nextBlock = `<button type="button" class="${clsPrevNextBase} js-column-archive-next bg-white text-[#2A892C]" aria-label="次のページへ">${svgNext}</button>`;
    }

    const pageItems = this._buildPageItems(maxPages, paged);
    const middle = pageItems
      .map((item) => {
        if (item.ellipsis) {
          return `<span class="${clsEllipsis}" aria-hidden="true">…</span>`;
        }
        const num = item.page;
        if (num === paged) {
          return `<span class="${clsCurrent}" aria-current="page">${num}</span>`;
        }
        return `<button type="button" class="${clsBtn}" data-page="${num}">${num}</button>`;
      })
      .join('');

    return `<nav class="${clsNav}" aria-label="コラムのページ送り">${prevBlock}${middle}${nextBlock}</nav>`;
  }

  _formatDate(iso) {
    const d = new Date(iso);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}.${m}.${day}`;
  }

  _decodeTitle(html) {
    const ta = document.createElement('textarea');
    ta.innerHTML = html;
    return ta.value;
  }

  _escapeHtml(str) {
    const el = document.createElement('div');
    el.textContent = str;
    return el.innerHTML;
  }

  _safeHref(link) {
    return /^https?:\/\//i.test(link || '') ? link : '#';
  }

  _getFeatured(post) {
    const emb = post._embedded;
    if (emb && emb['wp:featuredmedia'] && emb['wp:featuredmedia'][0]) {
      const m = emb['wp:featuredmedia'][0];
      const url = m.source_url || this.fallbackImage;
      const alt =
        (m.alt_text && String(m.alt_text)) ||
        (post.title && post.title.rendered
          ? this._decodeTitle(post.title.rendered)
          : '');
      return { url, alt: this._escapeHtml(alt) };
    }
    const titleRaw =
      post.title && post.title.rendered ? post.title.rendered : '';
    return {
      url: this.fallbackImage,
      alt: this._escapeHtml(this._decodeTitle(titleRaw)),
    };
  }

  _renderPost(post) {
    const titleRaw = post.title && post.title.rendered ? post.title.rendered : '';
    const title = this._escapeHtml(this._decodeTitle(titleRaw));
    const date = this._formatDate(post.date);
    const catIds = post.column_cat || [];
    const catName =
      catIds.length > 0
        ? this._escapeHtml(this.termNames[String(catIds[0])] || '')
        : '';
    const href = this._safeHref(post.link);
    const feat = this._getFeatured(post);
    const imgUrl = this._escapeHtml(feat.url);
    const imgAlt = feat.alt;

    const categoryBlock =
      catName !== ''
        ? `<p class="
          c-works-card-category
          shrink-0
          text-[1.3rem]
          bg-[#2A892C]
          !text-white
          px-[1.1rem]
          py-0
          leading-[1.5]

          sp:text-[1.3rem]
        ">${catName}</p>`
        : '';

    return `<li>
<a href="${href}" class="
  c-works-card

  bg-white
  rounded-[.4rem]
  block
  overflow-hidden
  [box-shadow:_0_.2rem_.4rem_0_rgba(0,_0,_0,_0.1)]
">
  <figure class="
    c-works-card-figure
    overflow-hidden
    relative
  ">
    <img class="c-works-card-img" src="${imgUrl}" alt="${imgAlt}">
  </figure>
  <div class="
    c-works-card-body
    p-[1.6rem]
    pb-[2.3rem]
    relative
  ">
    <div class="c-works-card-body-inner">
      <div class="
        c-works-card-categories
        flex
        items-center
        gap-[1rem]
      ">
        <time>${date}</time>
        ${categoryBlock}
      </div>
      <h3 class="
        c-works-card-title
        text-[1.6rem]
        font-bold
        mt-[0.6rem]
        leading-[1.5]

        sp:text-[1.8rem]
      ">${title}</h3>
    </div>
    <div class="c-works-card-body-footer">
      <div class="
        c-works-card-arrow
        c-arrow-flow
        absolute
        bottom-[1.5rem]
        right-[0.8rem]
        w-[4.8rem]
      ">
        <div class="c-arrow-flow__track">
          <span class="c-arrow-flow__item c-arrow-flow__item--reverse min-w-[4.8rem] [&_svg]:w-[4.2rem] [&_svg]:h-auto [&_svg_path]:stroke-[#2A892C] sp:[&_svg]:w-[4.2rem]">${COLUMN_CARD_ARROW_SVG}</span>
          <span class="c-arrow-flow__item min-w-[4.8rem] [&_svg]:w-[4.2rem] [&_svg]:h-auto [&_svg_path]:stroke-[#2A892C] sp:[&_svg]:w-[4.2rem]">${COLUMN_CARD_ARROW_SVG}</span>
        </div>
      </div>
    </div>
  </div>
</a>
</li>`;
  }
}

class Contact {
  constructor() {
    this._selectedKoumoku = null;
    this.init();
    this.initConfirm();
  }

  init() {
    // ラジオの選択値を記憶するリスナーをまず設定
    const initialChecked = document.querySelector('input[name="koumoku"]:checked');
    if (initialChecked) this._selectedKoumoku = initialChecked.value;

    document.addEventListener('change', (e) => {
      if (e.target.matches('input[name="koumoku"]')) {
        this._selectedKoumoku = e.target.value;
      }
    });

    // URLパラメータによるラジオ自動選択（リスナー設定後に実行）
    const params = new URLSearchParams(window.location.search);
    const type = params.get('type');
    if (!type) return;

    const typeMap = {
      security: '防犯製品について',
      development: '依頼開発について',
      recruitment: '採用について',
      media: 'メディアへの掲載・取材について',
      other: 'その他',
    };

    const koumokuValue = typeMap[type];
    if (!koumokuValue) return;

    const radio = document.querySelector(`[name="koumoku"][value="${koumokuValue}"]`);
    if (radio) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  initConfirm() {
    const span = document.getElementById('cf7msm_koumoku');

    // 確認ページ以外（#cf7msm_koumoku がない = 入力ページ）は何もしない
    if (!span) return;

    if (span.textContent.trim()) {
      // すでに値がある場合は即実行
      this._toggleConfirmGroups(span.textContent.trim());
    } else {
      // Webhead プラグインが JS でスパンに値をセットするまで待つ
      const observer = new MutationObserver(() => {
        const value = span.textContent.trim();
        if (value) {
          observer.disconnect();
          this._toggleConfirmGroups(value);
        }
      });
      observer.observe(span, { childList: true, characterData: true, subtree: true });

      // 5秒以内に値が来なければ監視終了
      setTimeout(() => observer.disconnect(), 5000);
    }
  }

  _toggleConfirmGroups(koumokuValue) {
    const showSecurity = koumokuValue
      .replace(/[\s\u3000]+/g, '')
      .includes('防犯製品');

    const securityGroup = document.querySelector('[data-confirm-group="security"]');
    const normalGroup = document.querySelector('[data-confirm-group="normal"]');

    if (securityGroup) securityGroup.style.display = showSecurity ? '' : 'none';
    if (normalGroup) normalGroup.style.display = showSecurity ? 'none' : '';
  }
}

class HonbunStepFlow {
  constructor() {
    this.roots = document.querySelectorAll('.js-honbun-step-flow-root');
    if (!this.roots.length) {
      return;
    }
    this.mq = window.matchMedia('(max-width: 767px)');
    this.swipers = new WeakMap();
    this.refresh = this.refresh.bind(this);
    this.mq.addEventListener('change', this.refresh);
    this.roots.forEach((root) => this.mount(root));
  }

  mount(root) {
    this.destroy(root);
    if (!this.mq.matches) {
      return;
    }
    const el = root.querySelector('.js-honbun-step-swiper');
    if (!el) {
      return;
    }
    const slideCount = el.querySelectorAll('.swiper-slide').length;
    if (slideCount < 2) {
      return;
    }
    const prev = root.querySelector('.js-honbun-step-prev');
    const next = root.querySelector('.js-honbun-step-next');
    const swiper = new Swiper(el, {
      slidesPerView: 'auto',
      centeredSlides: true,
      spaceBetween: 16,
      speed: 450,
      watchOverflow: true,
      navigation: {
        prevEl: prev,
        nextEl: next,
      },
    });
    this.swipers.set(root, swiper);
  }

  destroy(root) {
    const s = this.swipers.get(root);
    if (s) {
      s.destroy(true, true);
      this.swipers.delete(root);
    }
  }

  refresh() {
    this.roots.forEach((root) => this.mount(root));
  }
}

/**
 * お知らせアーカイブ: カテゴリは URL を変えず REST で一覧を差し替え（ページ全体の再読み込みなし）
 * 件数: PC 10 件 / ページ、SP（viewport max-width:767px）8 件 / ページ（enqueue の値と Tailwind `sp` に合わせる）
 */
class NewsArchive {
  constructor() {
    this.root = document.querySelector('.js-news-archive');
    if (!this.root || typeof sanokikoNewsArchive === 'undefined') {
      return;
    }

    this._reqId = 0;
    this._activeTermId = null;
    this._currentCategoryPage = 1;
    this._currentCategoryMaxPages = 1;

    this.pcPerPage = sanokikoNewsArchive.postsPerPagePc || 10;
    this.spPerPage = sanokikoNewsArchive.postsPerPageSp || 8;

    this.listEl = this.root.querySelector('.js-news-archive-list');
    this.anchorEl = document.getElementById('archive-anchor');
    this.pagingWrap = this.root.querySelector('.js-news-archive-paging');
    this.filters = this.root.querySelectorAll('.js-news-cat-filter');
    this.restUrl = sanokikoNewsArchive.restUrl;
    this.termNames = sanokikoNewsArchive.termNames || {};
    this.emptyMessage =
      sanokikoNewsArchive.emptyMessage || '該当するお知らせはありません。';

    if (!this.listEl || !this.filters.length) {
      return;
    }

    this.initialListHtml = this.listEl.innerHTML;
    this.initialPagingHtml = this.pagingWrap ? this.pagingWrap.innerHTML : '';

    this._loadingOverlay = this._createLoadingOverlay();
    this.root.style.position = 'relative';
    this.root.appendChild(this._loadingOverlay);

    this._bind();
    this._bindPagingDelegation();
    this._bindViewportChange();
  }

  _createLoadingOverlay() {
    const overlay = document.createElement('div');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('role', 'status');
    Object.assign(overlay.style, {
      position: 'absolute',
      inset: '0',
      zIndex: '10',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(255,255,255,0.75)',
      opacity: '0',
      pointerEvents: 'none',
      transition: 'opacity 0.2s',
    });

    const spinner = document.createElement('span');
    Object.assign(spinner.style, {
      display: 'block',
      width: '4rem',
      height: '4rem',
      borderRadius: '50%',
      border: '.4rem solid #e0e0e0',
      borderTopColor: '#2A892C',
      animation: 'news-archive-spin 0.8s linear infinite',
    });
    overlay.appendChild(spinner);

    if (!document.getElementById('news-archive-spin-style')) {
      const style = document.createElement('style');
      style.id = 'news-archive-spin-style';
      style.textContent =
        '@keyframes news-archive-spin{to{transform:rotate(360deg)}}';
      document.head.appendChild(style);
    }

    return overlay;
  }

  _showLoading() {
    this._loadingOverlay.style.opacity = '1';
    this._loadingOverlay.style.pointerEvents = 'auto';
  }

  _hideLoading() {
    this._loadingOverlay.style.opacity = '0';
    this._loadingOverlay.style.pointerEvents = 'none';
  }

  _getPerPage() {
    return window.matchMedia('(max-width: 767px)').matches
      ? this.spPerPage
      : this.pcPerPage;
  }

  /**
   * カテゴリ絞り込み・ページ送り後、#archive-anchor までスクロール（無ければ一覧 ul）
   */
  _scrollToArchiveAnchor() {
    const el = this.anchorEl || this.listEl;
    if (!el) return;
    const prefersReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    requestAnimationFrame(() => {
      el.scrollIntoView({
        behavior: prefersReduced ? 'auto' : 'smooth',
        block: 'start',
      });
    });
  }

  _bind() {
    this.filters.forEach((btn) => {
      btn.addEventListener('click', () => {
        const termId = btn.dataset.termId;
        this._activateFilter(btn);
        if (!termId) {
          this._showAll();
        } else {
          this._loadCategory(termId, 1);
        }
      });
    });
  }

  _bindPagingDelegation() {
    if (!this.pagingWrap) return;
    this.pagingWrap.addEventListener('click', (e) => {
      if (this._activeTermId == null) return;
      const pageBtn = e.target.closest('.js-news-archive-page');
      if (pageBtn) {
        const p = parseInt(pageBtn.dataset.page, 10);
        if (!Number.isNaN(p)) {
          this._loadCategory(this._activeTermId, p);
        }
        return;
      }
      if (e.target.closest('.js-news-archive-prev')) {
        if (this._currentCategoryPage > 1) {
          this._loadCategory(this._activeTermId, this._currentCategoryPage - 1);
        }
        return;
      }
      if (e.target.closest('.js-news-archive-next')) {
        if (this._currentCategoryPage < this._currentCategoryMaxPages) {
          this._loadCategory(this._activeTermId, this._currentCategoryPage + 1);
        }
      }
    });
  }

  _bindViewportChange() {
    let prev = this._getPerPage();
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = () => {
      const next = this._getPerPage();
      if (next === prev) return;
      prev = next;
      if (this._activeTermId != null) {
        this._loadCategory(this._activeTermId, 1);
      }
    };
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
    } else {
      mq.addListener(onChange);
    }
  }

  _activateFilter(activeBtn) {
    this.filters.forEach((b) => {
      const on = b === activeBtn;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (on) {
        b.setAttribute('aria-current', 'true');
      } else {
        b.removeAttribute('aria-current');
      }
    });
  }

  _showAll() {
    this._activeTermId = null;
    this._reqId += 1;
    this._hideLoading();
    this.listEl.innerHTML = this.initialListHtml;
    if (this.pagingWrap) {
      this.pagingWrap.innerHTML = this.initialPagingHtml;
      this.pagingWrap.hidden = false;
    }
  }

  _clearPaging() {
    if (this.pagingWrap) {
      this.pagingWrap.innerHTML = '';
      this.pagingWrap.hidden = true;
    }
  }

  _setEmpty() {
    this.listEl.innerHTML = `<li class="text-center text-[1.4rem] text-[#888888] py-[2rem]">${this.emptyMessage}</li>`;
    this._clearPaging();
    this._scrollToArchiveAnchor();
  }

  async _loadCategory(termId, page = 1) {
    const req = ++this._reqId;
    this._activeTermId = termId;
    this._showLoading();

    let result;
    try {
      result = await this._fetchPostsPage(termId, page);
    } catch (e) {
      if (req !== this._reqId) return;
      this._hideLoading();
      this._setEmpty();
      return;
    }

    if (req !== this._reqId) return;
    this._hideLoading();

    const { posts, totalPages } = result;
    this._currentCategoryPage = page;
    this._currentCategoryMaxPages = Math.max(1, totalPages);

    if (!posts.length) {
      this._setEmpty();
      return;
    }

    this.listEl.innerHTML = posts.map((p) => this._renderPost(p)).join('');

    if (this.pagingWrap) {
      this.pagingWrap.hidden = false;
      this.pagingWrap.innerHTML = this._renderPagingNav(page, this._currentCategoryMaxPages);
    }

    this._scrollToArchiveAnchor();
  }

  async _fetchPostsPage(termId, page) {
    const perPage = this._getPerPage();
    const url = new URL(this.restUrl, window.location.origin);
    url.searchParams.set('per_page', String(perPage));
    url.searchParams.set('page', String(page));
    url.searchParams.set('news_cat', String(termId));

    const res = await fetch(url.toString(), {
      credentials: 'same-origin',
    });

    if (!res.ok) {
      throw new Error('REST request failed');
    }

    const totalPages = parseInt(res.headers.get('X-WP-TotalPages') || '0', 10);
    const posts = await res.json();
    return {
      posts: Array.isArray(posts) ? posts : [],
      totalPages: Number.isFinite(totalPages) ? totalPages : 0,
    };
  }

  _buildPageItems(maxPages, paged) {
    if (maxPages <= 1) return [];
    const show = { 1: true, [maxPages]: true };
    const left = Math.max(2, paged - 2);
    const right = Math.min(maxPages - 1, paged + 2);
    for (let i = left; i <= right; i++) show[i] = true;
    const keys = Object.keys(show)
      .map(Number)
      .sort((a, b) => a - b);
    const items = [];
    let prevKey = 0;
    for (const p of keys) {
      if (prevKey && p - prevKey > 1) {
        items.push({ ellipsis: true });
      }
      items.push({ page: p });
      prevKey = p;
    }
    return items;
  }

  _renderPagingNav(paged, maxPages) {
    if (maxPages <= 1) return '';

    const clsNav =
      'flex justify-center items-center gap-[.6rem] [&_*]:font-outfit';
    const clsPrevNextBase =
      'w-[3.6rem] h-[3.6rem] sp:w-[3rem] sp:h-[3rem] rounded-full flex items-center justify-center font-bold text-[1.4rem] duration-300 pc:hover:opacity-70';
    const clsBtn =
      'js-news-archive-page w-[3.6rem] h-[3.6rem] sp:w-[3rem] sp:h-[3rem] rounded-full flex items-center justify-center font-normal text-white text-[1.8rem] duration-300 [background:_radial-gradient(94.38%_94.38%_at_19.25%_6.75%,_#3DA73F_0%,_#2A892C_100%)] border border-[#CCCCCC] pc:hover:opacity-70';
    const clsCurrent =
      'w-[3.6rem] h-[3.6rem] sp:w-[3rem] sp:h-[3rem] rounded-full flex items-center justify-center font-normal text-[1.8rem] duration-300 bg-white [border:_.1rem_solid_#2A892C] text-[#2A892C]';
    const clsEllipsis =
      'w-[3.6rem] h-[3.6rem] sp:w-[3rem] sp:h-[3rem] flex items-center justify-center text-[1.8rem] text-[#888888]';

    const svgPrev = `<span style="transform:scaleX(-1);display:flex;"><svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M1 1L6 6L1 11" stroke="#2A892C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg></span>`;
    const svgNext = `<svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M1 1L6 6L1 11" stroke="#2A892C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>`;

    let prevBlock;
    if (paged <= 1) {
      prevBlock = `<span class="${clsPrevNextBase} opacity-30 pointer-events-none" aria-hidden="true">${svgPrev}</span>`;
    } else {
      prevBlock = `<button type="button" class="${clsPrevNextBase} js-news-archive-prev bg-white text-[#2A892C]" aria-label="前のページへ">${svgPrev}</button>`;
    }

    let nextBlock;
    if (paged >= maxPages) {
      nextBlock = `<span class="${clsPrevNextBase} opacity-30 pointer-events-none" aria-hidden="true">${svgNext}</span>`;
    } else {
      nextBlock = `<button type="button" class="${clsPrevNextBase} js-news-archive-next bg-white text-[#2A892C]" aria-label="次のページへ">${svgNext}</button>`;
    }

    const pageItems = this._buildPageItems(maxPages, paged);
    const middle = pageItems
      .map((item) => {
        if (item.ellipsis) {
          return `<span class="${clsEllipsis}" aria-hidden="true">…</span>`;
        }
        const num = item.page;
        if (num === paged) {
          return `<span class="${clsCurrent}" aria-current="page">${num}</span>`;
        }
        return `<button type="button" class="${clsBtn}" data-page="${num}">${num}</button>`;
      })
      .join('');

    return `<nav class="${clsNav}" aria-label="お知らせのページ送り">${prevBlock}${middle}${nextBlock}</nav>`;
  }

  _formatDate(iso) {
    const d = new Date(iso);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}.${m}.${day}`;
  }

  _decodeTitle(html) {
    const ta = document.createElement('textarea');
    ta.innerHTML = html;
    return ta.value;
  }

  _escapeHtml(str) {
    const d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }

  _safeHref(link) {
    return /^https?:\/\//i.test(link || '') ? link : '#';
  }

  _renderPost(post) {
    const titleRaw = post.title && post.title.rendered ? post.title.rendered : '';
    const title = this._escapeHtml(this._decodeTitle(titleRaw));
    const date = this._formatDate(post.date);
    const catIds = post.news_cat || [];
    const catName =
      catIds.length > 0
        ? this._escapeHtml(this.termNames[String(catIds[0])] || '')
        : '';
    const href = this._safeHref(post.link);

    return `<li>
<a href="${href}" class="
  bg-[#F4F4F4]
  py-[1.9rem]
  pl-[2rem]
  pr-[3rem]
  rounded-[.4rem]
  relative
  block

  after:absolute
  after:top-1/2
  after:-translate-y-1/2
  after:right-[2rem]
  after:w-[.6rem]
  after:h-[1.1rem]
  after:[background:_url('../images/common/ic-arrow-right.svg')_no-repeat_center_center_/_contain]

  sp:after:right-[1.5rem]

  pc:[&_p]:hover:text-[#2A892C]
">
  <div class="
    flex
    gap-[1rem]
  ">
    <p class="
      shrink-0
      text-[1.3rem]
      text-[#888888]
      font-outfit

      pc:duration-[.3s]
    ">${date}</p>
    <p class="
      shrink-0
      text-[1.4rem]
      bg-white
      text-[#2A892C]
      px-[1.1rem]
      rounded-[9999px]
      leading-[1.5]
      [&:empty]:hidden
    ">${catName}</p>
  </div>
  <p class="
    line-clamp-1
    mt-[0.5rem]

    pc:duration-[.3s]
  ">${title}</p>
</a>
</li>`;
  }
}

class Recruit {
  constructor() {
    if ($(".js-jobs-swiper").length > 0) {
      this.event();
    }
  }
  event() {
    let swiper = null;
    const mq = window.matchMedia('(max-width: 767px)');

    const initSwiper = () => {
      if (mq.matches) {
        if (!swiper) {
          swiper = new Swiper(".js-jobs-swiper", {
            centeredSlides: true,
            slidesPerView: 1.25,
            spaceBetween: 16,
            loop: true,
            loopAdditionalSlides: 3,
            speed: 500,
            autoplay: {
              delay: 3000,
              disableOnInteraction: false,
            },
            pagination: {
              el: ".js-jobs-swiper-pagination",
              clickable: true,
            },
            navigation: {
              nextEl: ".js-jobs-swiper-next",
              prevEl: ".js-jobs-swiper-prev",
            },
          });
        }
      } else {
        if (swiper) {
          swiper.destroy(true, true);
          swiper = null;
        }
      }
    };

    initSwiper();
    mq.addEventListener('change', initSwiper);
  }
}

class Top {
  constructor(el,opt){
    this.el = el;
    if($("." + this.el).length > 0){
      this.event();
    }
  }
  event(){
    const AUTOPLAY_DELAY = 3000;
    const AUTOPLAY_SPEED = 500;
    let heroProgressRAF = null;

    // 初回ロード判定（Swiper初期化前に判定が必要）
    const FIRST_LOAD_KEY = 'firstLoadTimestamp';
    const THIRTY_MINUTES = 30 * 60 * 1000;
    const now = Date.now();
    const firstLoadTimestamp = localStorage.getItem(FIRST_LOAD_KEY);
    const isFirstLoad = !firstLoadTimestamp || (now - parseInt(firstLoadTimestamp, 10)) > THIRTY_MINUTES;
    if (isFirstLoad) {
      localStorage.setItem(FIRST_LOAD_KEY, String(now));
    }

    function updateHeroText(swiper) {
      const activeIndex = swiper.realIndex;
      document.querySelectorAll('.js-hero-text').forEach((el) => {
        const isActive = parseInt(el.dataset.index, 10) === activeIndex;
        el.classList.toggle('opacity-0', !isActive);
        el.classList.toggle('opacity-100', isActive);
      });
    }

    const heroSwiper = new Swiper(".js-hero-swiper", {
      loop: true,
      slidesPerView: 1,
      speed: AUTOPLAY_SPEED,
      effect: "fade",
      fadeEffect: {
        crossFade: true
      },
      autoplay: {
        delay: AUTOPLAY_DELAY,
        disableOnInteraction: false,
        pauseOnMouseEnter: false,      // 追加: ホバーしても止まらない
        stopOnLastSlide: false,        // 追加: 最後で止まらない
        waitForTransition: false       // 追加: トランジション中もキューしない
      },
      navigation: {
        nextEl: ".js-hero-swiper-next",
        prevEl: ".js-hero-swiper-prev",
      },
      on: {
        slideChangeTransitionEnd(swiper) {
          startHeroProgressAnimation(swiper, 'slideChangeTransitionEnd');
        },
        slideChangeTransitionStart(swiper) {
          updateHeroText(swiper);
        },
        init(swiper) {
          updateHeroText(swiper);
          if (isFirstLoad) {
            swiper.autoplay.stop();
          } else {
            startHeroProgressAnimation(swiper, 'init');
          }
          document.querySelectorAll(".hero-swiper-progress__segment").forEach((segment) => {
            segment.addEventListener("click", () => {
              const index = parseInt(segment.dataset.index, 10);
              swiper.slideToLoop(index);
            });
          });
        },
        slideNextTransitionStart(swiper) {

          if (swiper.activeIndex === swiper.slides.length - 1) {
            $('.hero-swiper-progress__segment-fill').animate({ opacity: 0 }, AUTOPLAY_SPEED);
            setTimeout(() => {
              $('.hero-swiper-progress__segment-fill').css('opacity', '1');
            }, AUTOPLAY_SPEED + 100);
          }
          
        },
      },
    });

    function startHeroProgressAnimation(swiper, type) {
      if (heroProgressRAF) {
        cancelAnimationFrame(heroProgressRAF);
        heroProgressRAF = null;
      }

      const segments = document.querySelectorAll(".hero-swiper-progress__segment-fill");
      const activeIndex = swiper.realIndex;
      const startTime = performance.now();

      const animate = (timestamp) => {
        const elapsed = timestamp - startTime;
        let progress;
        if(type === 'init') {
          progress = Math.min((elapsed / AUTOPLAY_DELAY) * 100, 100);
        } else {
          progress = Math.min((elapsed / (AUTOPLAY_DELAY - AUTOPLAY_SPEED)) * 100, 100);
        }

        segments.forEach((fill, i) => {
          if (i < activeIndex) {
            fill.style.width = "100%";
          } else if (i === activeIndex) {
            fill.style.width = `${progress}%`;
          } else {
            fill.style.width = "0%";
          }
        });

        if (progress < 100) {
          heroProgressRAF = requestAnimationFrame(animate);
        }
      };

      heroProgressRAF = requestAnimationFrame(animate);
    }

    const PC_BREAKPOINT = 768;
    const PC_MIN_SLIDES = 3;
    const SP_MIN_SLIDES = 2;
    const PC_WORKS_NAV_MIN_SLIDES = 4;
    const SP_WORKS_NAV_MIN_SLIDES = 2;

    document.querySelectorAll(".js-product-swiper").forEach((el) => {
      const container = el.closest(".js-product-swiper-container");
      if (!container) return;
      const navWrapper = container.querySelector(".js-product-swiper-nav");
      const count = el.querySelectorAll(".swiper-slide").length;
      if (count === 0) {
        if (navWrapper) navWrapper.style.display = 'none';
        return;
      }

      const isPC = () => window.innerWidth >= PC_BREAKPOINT;
      const wantsSwiper = () => isPC() ? count >= PC_MIN_SLIDES : count >= SP_MIN_SLIDES;

      let swiperInstance = null;
      let swiperActive = null;

      const applyProductSwiper = () => {
        const enable = wantsSwiper();
        if (swiperActive === enable) {
          if (swiperInstance) swiperInstance.update();
          return;
        }
        swiperActive = enable;
        if (!enable) {
          if (swiperInstance) {
            swiperInstance.destroy(true, true);
            swiperInstance = null;
          }
          el.classList.add('is-product-static');
          if (navWrapper) navWrapper.style.display = 'none';
          return;
        }
        el.classList.remove('is-product-static');
        if (navWrapper) navWrapper.style.display = '';
        swiperInstance = new Swiper(el, {
          loop: true,
          slidesPerView: 1.3,
          speed: 500,
          loopAdditionalSlides: 10,
          navigation: {
            nextEl: container.querySelector(".js-product-swiper-next"),
            prevEl: container.querySelector(".js-product-swiper-prev"),
          },
          pagination: {
            el: container.querySelector(".js-product-swiper-pagination"),
            type: "fraction",
          },
          breakpoints: {
            [PC_BREAKPOINT]: {
              slidesPerView: 2,
            },
          },
        });
      };

      applyProductSwiper();
      window.addEventListener('resize', applyProductSwiper);
    });

    // トップバナー: 表示中ブレークポイントのみ初期化（非表示時初期化だとページネーションが壊れる）
    {
      const mqPc = window.matchMedia(`(min-width: ${PC_BREAKPOINT}px)`);
      let bannerSwiperPc = null;
      let bannerSwiperSp = null;

      const destroyBannerSwiper = (instance) => {
        if (!instance) return null;
        instance.destroy(true, true);
        return null;
      };

      const bannerAutoplay = {
        delay: 3000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      };

      const initBannerSwiperPc = () => {
        const el = document.querySelector(".js-banner-swiper-pc");
        const container = el?.closest(".js-banner-swiper-container-pc");
        if (!el || !container) return null;
        return new Swiper(el, {
          slidesPerView: 4,
          spaceBetween: 10,
          speed: 500,
          loop: true,
          loopAdditionalSlides: 4,
          watchOverflow: true,
          autoplay: bannerAutoplay,
          navigation: {
            nextEl: container.querySelector(".js-banner-swiper-next-pc"),
            prevEl: container.querySelector(".js-banner-swiper-prev-pc"),
          },
          pagination: {
            el: container.querySelector(".js-banner-swiper-pagination-pc"),
            clickable: true,
          },
        });
      };

      const initBannerSwiperSp = () => {
        const el = document.querySelector(".js-banner-swiper-sp");
        const container = el?.closest(".js-banner-swiper-container-sp");
        if (!el || !container) return null;
        return new Swiper(el, {
          slidesPerView: 1,
          spaceBetween: 0,
          speed: 500,
          loop: true,
          watchOverflow: true,
          autoplay: bannerAutoplay,
          pagination: {
            el: container.querySelector(".js-banner-swiper-pagination-sp"),
            clickable: true,
          },
        });
      };

      const applyBannerSwipers = () => {
        if (mqPc.matches) {
          bannerSwiperSp = destroyBannerSwiper(bannerSwiperSp);
          if (!bannerSwiperPc) {
            bannerSwiperPc = initBannerSwiperPc();
          } else {
            bannerSwiperPc.update();
          }
        } else {
          bannerSwiperPc = destroyBannerSwiper(bannerSwiperPc);
          if (!bannerSwiperSp) {
            bannerSwiperSp = initBannerSwiperSp();
          } else {
            bannerSwiperSp.update();
          }
        }
      };

      applyBannerSwipers();
      mqPc.addEventListener("change", applyBannerSwipers);
    }

    document.querySelectorAll('.js-works-cat-tabs-scroll').forEach((scrollEl) => {
      const progressFill = scrollEl.closest('.js-works-cat-tabs-wrap')?.querySelector('.js-works-cat-tabs-progress-fill');
      if (!progressFill) return;

      const updateCatTabsScrollProgress = () => {
        const maxScroll = scrollEl.scrollWidth - scrollEl.clientWidth;
        const progress = maxScroll > 0 ? scrollEl.scrollLeft / maxScroll : 1;
        progressFill.style.width = `${Math.min(100, Math.max(0, progress * 100))}%`;
      };

      scrollEl.addEventListener('scroll', updateCatTabsScrollProgress, { passive: true });
      window.addEventListener('resize', updateCatTabsScrollProgress);
      updateCatTabsScrollProgress();
    });

    document.querySelectorAll(".js-works-swiper").forEach((el) => {
      const container = el.closest(".js-works-swiper-container");
      if (!container) return;
      const progressFill = container.querySelector(".js-works-swiper-progress-fill");
      const navWrapper   = container.querySelector(".js-works-swiper-nav");
      const footerEl     = container.querySelector(".js-works-swiper-footer");
      const totalCount = parseInt(el.dataset.count, 10) || el.querySelectorAll(".swiper-slide").length || 0;
      const getVisibleCount = () => el.querySelectorAll(".swiper-slide:not(.is-filtered-out)").length;
      let visibleCount = totalCount;
      container.dataset.worksSlides = visibleCount;

      const isPC = () => window.innerWidth >= PC_BREAKPOINT;

      const wantsSwiper = () => visibleCount > 0 && (
        isPC()
          ? visibleCount >= PC_WORKS_NAV_MIN_SLIDES
          : visibleCount >= SP_MIN_SLIDES
      );

      const shouldShowNav = () => isPC()
        ? visibleCount >= PC_WORKS_NAV_MIN_SLIDES
        : visibleCount >= SP_WORKS_NAV_MIN_SLIDES;

      const updateNav = () => {
        const show = shouldShowNav();
        if (navWrapper) {
          navWrapper.style.display = show ? '' : 'none';
        }
        // ナビ非表示時は 1fr 列にボタンだけが残って横全幅になるのを防ぐ
        if (footerEl) {
          footerEl.classList.toggle('is-nav-hidden', !show);
        }
      };

      const updateWorksProgress = (swiper) => {
        if (!progressFill) return;
        if (visibleCount <= 1) {
          progressFill.style.width = '100%';
          return;
        }
        if (swiper && typeof swiper.progress === 'number') {
          progressFill.style.width = `${Math.min(100, Math.max(0, swiper.progress * 100))}%`;
          return;
        }
        progressFill.style.width = '0%';
      };

      let swiperInstance = null;
      let swiperActive = null;

      const applyWorksPickupSwiper = () => {
        updateNav();
        const enable = wantsSwiper();

        if (swiperActive === enable) {
          if (swiperInstance) {
            swiperInstance.update();
            updateWorksProgress(swiperInstance);
          } else {
            // Swiperを使わない状態でも、バーだけは常に「正しい見た目」にしておく
            updateWorksProgress({ realIndex: 0 });
          }
          return;
        }
        swiperActive = enable;
        if (!enable) {
          if (swiperInstance) {
            swiperInstance.destroy(true, true);
            swiperInstance = null;
          }
          el.classList.add('is-pickup-static');
          updateWorksProgress({ realIndex: 0 });
          return;
        }
        el.classList.remove('is-pickup-static');

        // container配下のナビを都度取得（SP含めインスタンスごとに紐付け）
        const nextEl = container.querySelector(".js-works-swiper-next");
        const prevEl = container.querySelector(".js-works-swiper-prev");

        swiperInstance = new Swiper(el, {
          slidesPerView: 1,
          centeredSlides: false,
          speed: 500,
          ...(nextEl && prevEl
            ? {
                navigation: {
                  nextEl,
                  prevEl,
                },
              }
            : {}),
          breakpoints: {
            [PC_BREAKPOINT]: {
              slidesPerView: 3.3,
            },
          },
          on: {
            init(swiper) {
              updateWorksProgress(swiper);
            },
            slideChangeTransitionStart(swiper) {
              updateWorksProgress(swiper);
            },
            slideChangeTransitionEnd(swiper) {
              updateWorksProgress(swiper);
            },
            progress(swiper, progress) {
              if (!progressFill) return;
              progressFill.style.width = `${Math.min(100, Math.max(0, progress * 100))}%`;
            },
          },
        });
      };

      const tabsEl = container.closest('.c-inner')?.querySelector('.js-works-cat-tabs');
      if (tabsEl) {
        tabsEl.querySelectorAll('.js-works-cat-tab').forEach((tab) => {
          tab.addEventListener('click', () => {
            const category = tab.dataset.category || '';
            tabsEl.querySelectorAll('.js-works-cat-tab').forEach((button) => {
              const isActive = button === tab;
              button.classList.toggle('is-active', isActive);
              button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            });

            el.querySelectorAll('.swiper-slide').forEach((slide) => {
              const categories = JSON.parse(slide.dataset.categories || '[]');
              const match = !category || categories.includes(category);
              slide.classList.toggle('is-filtered-out', !match);
            });

            visibleCount = getVisibleCount();
            container.dataset.worksSlides = visibleCount;

            swiperActive = null;
            applyWorksPickupSwiper();
          });
        });
      }

      if (totalCount === 0) return;

      applyWorksPickupSwiper();
      window.addEventListener('resize', applyWorksPickupSwiper);
    });

    // 初回ロード時のGSAPアニメーション
    if (isFirstLoad) {
      const tl = gsap.timeline({
        onStart: () => {
          scrollBan();
        },
        onComplete: () => {
          heroSwiper.autoplay.start();
          startHeroProgressAnimation(heroSwiper);
        },
      });

      tl.set('header, .js-hero, .js-hero-bottom', {
        opacity: 0,
      });

      tl.to('.js-loading-overlay img', {
        opacity: 1,
        duration: 0.3,
        delay: 0.3,
        ease: 'power2.inOut',
        onComplete: () => {
          document.documentElement.classList.remove('is-first-load');
        },
      });
      tl.to('.js-loading-overlay', {
        clipPath: 'inset(100% 0 0 0)',
        duration: 0.5,
        delay: 0.8,
        ease: 'linear',
      });
      tl.to('header, .js-hero, .js-hero-bottom', {
        opacity: 1,
        duration: 0.5,
        delay: 0.5,
        ease: 'power2.inOut',
        onComplete: () => {
          scrollBan();
        },
      });
    } else {
      $('.js-loading-overlay').fadeOut(300);
    }
  }
}

class Works {
  constructor(el,opt){
    this.el = el;
    if($("." + this.el).length > 0){
      this.event();
      this.filterInit();
      this.paginateInit();
      this.urlParamInit();
    }
  }
  event(){
    $('.js-works-tab-container:nth-of-type(n + 2)').hide();

    const worksSwipers = [];

    $('.js-tab-trigger').click((e) => {
      const index = $(e.currentTarget).index();
      $(e.currentTarget).addClass("is-active").siblings().removeClass("is-active");
      $('.js-works-tab-container').hide();
      const tabContainer = $('.js-works-tab-container').eq(index);
      const progressFill = tabContainer.find(".js-works-swiper-progress-fill")[0];
      if (progressFill) {
        const totalSlides = parseInt(tabContainer.attr("data-works-slides") || "1", 10);
        progressFill.style.transition = "none";
        progressFill.style.width = `${(1 / totalSlides) * 100}%`;
        progressFill.offsetHeight;
        progressFill.style.transition = "";
      }
      const swiper = worksSwipers[index];
      if (swiper && swiper.update) {
        swiper.update();
        swiper.slideToLoop(0, 0);
      }
      tabContainer.fadeIn(400);
    });

    document.querySelectorAll(".js-works-swiper").forEach((el, index) => {
      const container = el.closest(".js-works-swiper-container");
      if (!container) return;
      const progressFill = container.querySelector(".js-works-swiper-progress-fill");
      const totalSlides = parseInt(el.dataset.count, 10) || el.querySelectorAll(".swiper-slide").length || 1;
      container.dataset.worksSlides = totalSlides;

      const prevBtn = container.querySelector(".js-works-swiper-prev");
      const nextBtn = container.querySelector(".js-works-swiper-next");
      const navArea = container.querySelector(".js-works-swiper-progress")?.closest(".flex");

      // 3件以下: 静止表示（Swiperなし）
      if (totalSlides <= 3) {
        el.classList.add('is-pickup-static');
        if (navArea) navArea.style.display = 'none';
        worksSwipers[index] = null;
        return;
      }

      // 4件以上: Swiper初期化
      const updateWorksProgress = (swiper) => {
        if (!progressFill) return;
        progressFill.style.width = `${swiper.progress * 100}%`;
      };

      const updateNavState = (swiper) => {
        if (prevBtn) prevBtn.classList.toggle('opacity-30', swiper.isBeginning);
        if (nextBtn) nextBtn.classList.toggle('opacity-30', swiper.isEnd);
      };

      const swiper = new Swiper(el, {
        slidesPerView: 1.4,
        speed: 500,
        navigation: {
          nextEl: nextBtn,
          prevEl: prevBtn,
        },
        breakpoints: {
          768: {
            slidesPerView: 3.3,
          },
        },
        on: {
          init(swiper) {
            updateWorksProgress(swiper);
            updateNavState(swiper);
          },
          slideChangeTransitionStart(swiper) {
            updateWorksProgress(swiper);
            updateNavState(swiper);
          },
        },
      });
      worksSwipers[index] = swiper;
    });
  }
  paginateInit(){
    // PC: 9件/ページ（10件以上でページネーション表示）
    // SP: 8件/ページ（9件以上でページネーション表示）
    const getItemsPerPage = () => (window.innerWidth <= 767 ? 8 : 9);
    let currentPage = 1;
    const cards = [...document.querySelectorAll('.js-works-card')];
    const container = document.querySelector('.js-works-pagination');
    if (!container || cards.length === 0) return;

    cards.forEach(c => { if (!c.dataset.filtered) c.dataset.filtered = '1'; });

    const getFilteredCards = () => cards.filter(c => c.dataset.filtered !== '0');
    const getTotalPages    = () => Math.max(1, Math.ceil(getFilteredCards().length / getItemsPerPage()));

    const updateDisplay = () => {
      const perPage  = getItemsPerPage();
      const filtered = getFilteredCards();
      const total    = getTotalPages();
      if (currentPage > total) currentPage = total;
      const start = (currentPage - 1) * perPage;
      cards.forEach(card => {
        const idx = filtered.indexOf(card);
        card.style.display = (card.dataset.filtered === '0' || idx < start || idx >= start + perPage)
          ? 'none' : '';
      });

      // 件数表示更新
      const visibleCount = document.querySelector('.js-works-visible-count');
      const totalCount   = document.querySelector('.js-works-total-count');
      if (visibleCount) {
        const displayStart = filtered.length === 0 ? 0 : start + 1;
        const displayEnd   = Math.min(start + perPage, filtered.length);
        visibleCount.textContent = filtered.length === 0 ? '0' : `${displayStart}〜${displayEnd}`;
      }
      if (totalCount) totalCount.textContent = filtered.length;

      renderPagination(total);
    };

    const scrollToAnchor = () => {
      const anchor = document.querySelector('#anchor-works');
      if (!anchor) return;

      // ページネーション再描画でフォーカスが残ると、ブラウザが下部へ戻すことがある
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      // html { scroll-behavior: smooth } を一時的に無効化し、確実に即時移動する
      const root = document.documentElement;
      const prevBehavior = root.style.scrollBehavior;
      root.style.scrollBehavior = 'auto';
      anchor.scrollIntoView({ block: 'start' });
      root.style.scrollBehavior = prevBehavior;
    };

    const goToPage = (page, { scroll = false } = {}) => {
      currentPage = Math.max(1, Math.min(page, getTotalPages()));
      updateDisplay();
      if (scroll) setTimeout(scrollToAnchor, 0);
    };

    this._goToPage = goToPage;

    // カードの表示切替による scroll anchoring がページ切替後のスクロールを打ち消すのを防ぐ
    const grid = document.querySelector('.js-works-grid');
    if (grid) grid.style.setProperty('overflow-anchor', 'none');

    const getPagesArray = (current, total) => {
      const range = [];
      for (let i = 1; i <= total; i++) {
        if (i === 1 || i === total || (i >= current - 2 && i <= current + 2)) range.push(i);
      }
      const result = [];
      let prev = null;
      for (const p of range) {
        if (prev !== null && p - prev > 1) result.push('...');
        result.push(p);
        prev = p;
      }
      return result;
    };

    const arrowSvg = `<svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L6 6L1 11" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

    const renderPagination = (total) => {
      if (total <= 1) { container.innerHTML = ''; return; }

      const pages       = getPagesArray(currentPage, total);
      const prevDisabled = currentPage === 1;
      const nextDisabled = currentPage === total;
      const btnBase     = 'w-[3.6rem] h-[3.6rem] rounded-full flex items-center justify-center font-bold duration-300';
      const arrowBase   = `${btnBase} bg-[#2A892C] text-white`;

      let html = '';

      html += `<button type="button" class="js-works-page-prev ${arrowBase} ${prevDisabled ? 'opacity-30 cursor-not-allowed' : 'pc:hover:opacity-70'}" ${prevDisabled ? 'disabled' : ''}>
        <span style="transform:scaleX(-1);display:flex;">${arrowSvg}</span>
      </button>`;

      for (const p of pages) {
        if (p === '...') {
          html += `<span class="w-[3.6rem] h-[3.6rem] flex items-center justify-center text-[1.4rem] text-[#888]">…</span>`;
        } else if (p === currentPage) {
          html += `<button type="button" class="js-works-page ${btnBase} text-[1.4rem] bg-[#2A892C] text-white" data-page="${p}">${p}</button>`;
        } else {
          html += `<button type="button" class="js-works-page ${btnBase} text-[1.4rem] bg-white text-[#333333] border border-[#CCCCCC] pc:hover:border-[#2A892C] pc:hover:text-[#2A892C]" data-page="${p}">${p}</button>`;
        }
      }

      html += `<button type="button" class="js-works-page-next ${arrowBase} ${nextDisabled ? 'opacity-30 cursor-not-allowed' : 'pc:hover:opacity-70'}" ${nextDisabled ? 'disabled' : ''}>
        ${arrowSvg}
      </button>`;

      container.innerHTML = html;

      container.querySelectorAll('.js-works-page[data-page]').forEach(btn => {
        btn.addEventListener('click', () => goToPage(parseInt(btn.dataset.page), { scroll: true }));
      });
      const prevBtn = container.querySelector('.js-works-page-prev');
      const nextBtn = container.querySelector('.js-works-page-next');
      if (prevBtn && !prevDisabled) prevBtn.addEventListener('click', () => goToPage(currentPage - 1, { scroll: true }));
      if (nextBtn && !nextDisabled) nextBtn.addEventListener('click', () => goToPage(currentPage + 1, { scroll: true }));
    };

    updateDisplay();

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateDisplay, 150);
    });
  }
  urlParamInit(){
    const filterEl = document.querySelector('.js-works-filter');
    if (!filterEl) return;

    const urlParams = new URLSearchParams(window.location.search);
    const catParam  = urlParams.get('cat');
    if (!catParam) return;

    const slugs = catParam.split(',').map(s => s.trim());
    filterEl.querySelectorAll('.js-works-filter-checkbox').forEach(checkbox => {
      if (slugs.includes(checkbox.dataset.slug)) {
        checkbox.checked = true;
      }
    });

    if (this._applyFilter) this._applyFilter();

    const anchor = document.querySelector('#anchor-works');
    if (anchor) {
      setTimeout(() => {
        anchor.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }
  filterInit(){
    const filterEl      = document.querySelector('.js-works-filter');
    if (!filterEl) return;

    // SP アコーディオン
    const toggleEl = filterEl.querySelector('.js-works-filter-toggle');
    const bodyEl   = filterEl.querySelector('.js-works-filter-body');
    const iconEl   = filterEl.querySelector('.js-works-filter-icon');
    const isSP     = () => window.innerWidth <= 767;

    if (toggleEl && bodyEl) {
      if (isSP()) {
        bodyEl.classList.add('is-open');
        if (iconEl) iconEl.style.transform = 'translateX(-50%) rotate(90deg)';
      }

      toggleEl.addEventListener('click', () => {
        if (!isSP()) return;
        const isOpen = bodyEl.classList.contains('is-open');
        if (isOpen) {
          $(bodyEl).slideUp(300);
          bodyEl.classList.remove('is-open');
          if (iconEl) iconEl.style.transform = 'translateX(-50%)';
        } else {
          $(bodyEl).slideDown(300);
          bodyEl.classList.add('is-open');
          if (iconEl) iconEl.style.transform = 'translateX(-50%) rotate(90deg)';
        }
      });
    }

    const submitBtn = filterEl.querySelector('.js-works-filter-submit');
    const clearBtn  = filterEl.querySelector('.js-works-filter-clear');
    const cards     = document.querySelectorAll('.js-works-card');

    const applyFilter = () => {
      const checkedBusinesses = [...filterEl.querySelectorAll('input[name="business[]"]:checked')].map(el => el.value);
      const checkedCategories = [...filterEl.querySelectorAll('input[name="category[]"]:checked')].map(el => el.value);

      cards.forEach(card => {
        const business   = card.dataset.business;
        const categories = JSON.parse(card.dataset.categories || '[]');

        const matchBusiness = checkedBusinesses.length === 0 || checkedBusinesses.includes(business);
        const matchCategory = checkedCategories.length === 0 || checkedCategories.every(c => categories.includes(c));

        card.dataset.filtered = (matchBusiness && matchCategory) ? '1' : '0';
      });

      if (this._goToPage) this._goToPage(1);
    };

    this._applyFilter = applyFilter;

    submitBtn.addEventListener('click', applyFilter);

    clearBtn.addEventListener('click', () => {
      filterEl.querySelectorAll('.js-works-filter-checkbox').forEach(el => el.checked = false);
      cards.forEach(card => { card.dataset.filtered = '1'; });
      if (this._goToPage) this._goToPage(1);
    });
  }
}

class WorksSingle {
  constructor(el, opt) {
    this.el = el;
    if ($('.' + this.el).length > 0) {
      this.galleryInit();
      this.pointsSwiperInit();
      this.relatedSwiperInit();
      this.youtubeInit();
    }
  }

  galleryInit() {
    const galleryEl = document.querySelector('.js-works-gallery-main');
    if (!galleryEl) return;

    const slides = galleryEl.querySelectorAll('.swiper-slide');
    const dots   = document.querySelectorAll('.js-gallery-dot');

    const updateDots = (index) => {
      dots.forEach((dot, i) => {
        dot.classList.toggle('bg-[#2A892C]', i === index);
        dot.classList.toggle('bg-[#BBBBBB]',  i !== index);
      });
    };

    const swiper = new Swiper(galleryEl, {
      slidesPerView: 1,
      speed: 500,
      loop: slides.length > 1,
      navigation: {
        nextEl: '.js-gallery-next',
        prevEl: '.js-gallery-prev',
      },
      on: {
        init(sw) {
          updateDots(sw.realIndex);
        },
        slideChange(sw) {
          updateDots(sw.realIndex);
        },
      },
    });

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => swiper.slideToLoop(i));
    });
  }

  pointsSwiperInit() {
    const swiperEl  = document.querySelector('.js-works-points-swiper');
    if (!swiperEl) return;

    const wrapper    = swiperEl.closest('.relative');
    const prevBtn    = wrapper?.querySelector('.js-points-prev');
    const nextBtn    = wrapper?.querySelector('.js-points-next');
    const dotsWrap   = wrapper?.querySelector('.js-points-dots-wrap');
    const dots       = wrapper ? wrapper.querySelectorAll('.js-points-dot') : [];
    const slideCount = swiperEl.querySelectorAll('.swiper-slide').length;
    const pcDisabled = slideCount <= 3;

    const updateDots = (index) => {
      dots.forEach((dot, i) => {
        dot.classList.toggle('bg-[#2A892C]', i === index);
        dot.classList.toggle('bg-[#BBBBBB]',  i !== index);
      });
    };

    const setNavVisible = (visible) => {
      [prevBtn, nextBtn, dotsWrap].forEach(el => {
        if (el) el.style.display = visible ? '' : 'none';
      });
    };

    const swiper = new Swiper(swiperEl, {
      slidesPerView: 1.35,
      spaceBetween: 15,
      speed: 500,
      centeredSlides: true,
      watchOverflow: true,
      loop: slideCount > 2,
      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn,
      },
      breakpoints: {
        768: {
          slidesPerView: 3,
          spaceBetween: 20,
          centeredSlides: false,
          enabled: !pcDisabled,
        },
      },
      on: {
        init(sw) {
          updateDots(sw.realIndex);
          setNavVisible(sw.enabled && !sw.isLocked);
        },
        slideChange(sw) {
          updateDots(sw.realIndex);
        },
        lock(sw) {
          setNavVisible(false);
        },
        unlock(sw) {
          setNavVisible(true);
          updateDots(sw.realIndex);
        },
        disable() {
          setNavVisible(false);
        },
        enable(sw) {
          setNavVisible(!sw.isLocked);
          updateDots(sw.realIndex);
        },
        breakpoint(sw) {
          setNavVisible(sw.enabled && !sw.isLocked);
          updateDots(sw.realIndex);
        },
      },
    });

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => swiper.slideTo(i));
    });
  }

  youtubeInit() {
    document.querySelectorAll('.js-youtube-wrap').forEach(wrap => {
      wrap.addEventListener('click', () => {
        const id = wrap.dataset.youtubeId;
        if (!id) return;
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.youtube.com/embed/${id}?autoplay=1`;
        iframe.setAttribute('allow', 'autoplay; encrypted-media');
        iframe.setAttribute('allowfullscreen', '');
        iframe.className = 'absolute inset-0 w-full h-full';
        wrap.innerHTML = '';
        wrap.appendChild(iframe);
      });
    });
  }

  relatedSwiperInit() {
    const swiperEl    = document.querySelector('.js-related-works-swiper');
    if (!swiperEl) return;

    const container    = swiperEl.closest('.js-related-swiper-container');
    const navWrap      = container ? container.querySelector('.js-related-swiper-nav') : null;
    const progressFill = container ? container.querySelector('.js-related-swiper-progress-fill') : null;
    const prevBtn      = container ? container.querySelector('.js-related-swiper-prev') : null;
    const nextBtn      = container ? container.querySelector('.js-related-swiper-next') : null;

    const count     = parseInt(swiperEl.dataset.count, 10) || 0;
    const pcEnabled = count >= 3;

    const updateProgress = (sw) => {
      if (!progressFill) return;
      progressFill.style.width = `${sw.progress * 100}%`;
    };

    const updateNav = (sw) => {
      if (prevBtn) prevBtn.classList.toggle('opacity-30', sw.isBeginning);
      if (nextBtn) nextBtn.classList.toggle('opacity-30', sw.isEnd);
    };

    const setNavVisible = (visible) => {
      if (navWrap) navWrap.style.display = visible ? '' : 'none';
    };

    new Swiper(swiperEl, {
      slidesPerView: 1.4,
      speed: 500,
      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn,
      },
      breakpoints: {
        768: {
          slidesPerView: 3.3,
          enabled: pcEnabled,
        },
      },
      on: {
        init(sw) {
          updateProgress(sw);
          updateNav(sw);
          setNavVisible(sw.enabled);
        },
        slideChangeTransitionStart(sw) {
          updateProgress(sw);
          updateNav(sw);
        },
        enable(sw) {
          setNavVisible(true);
          updateProgress(sw);
          updateNav(sw);
        },
        disable() {
          setNavVisible(false);
        },
        breakpoint(sw) {
          setNavVisible(sw.enabled);
        },
      },
    });
  }
}

class Common {
  constructor(el,opt){
    this.event();
  }
  event(){
    $('.js-drawer-trigger').click(function(){
      $('html').toggleClass('is-drawer');
      scrollBan();
    });

    $('.js-drawer-accordion').click(function(){
      $(this).next().slideToggle();
      $(this).find('.trigger').toggleClass('is-open');
    });

    // pcだけの場合のみ実行
    if (window.matchMedia('(min-width: 768px)').matches) {

      const showMega = (trigger, container) => {
        trigger.addClass('is-hover');
        container.addClass('is-hover');
      };
      const hideMega = (trigger, container) => {
        trigger.removeClass('is-hover');
        container.removeClass('is-hover');
      };

      $('.js-mega-trigger').hover(
        function(){
          let target = $(this).data('mega-target');
          showMega($(this), $("[data-mega-container='"+target+"']"));
        },
        function(){
          let target = $(this).data('mega-target');
          hideMega($(this), $("[data-mega-container='"+target+"']"));
        }
      );

      $('.js-mega-container').hover(
        function(){
          let container = $(this).data('mega-container');
          showMega($("[data-mega-target='"+container+"']"), $(this));
        },
        function(){
          let container = $(this).data('mega-container');
          hideMega($("[data-mega-target='"+container+"']"), $(this));
        }
      );
    }

    // 20pxスクロールしたら、htmlに.is-scrollをつけたい
    $(window).on('scroll', function() {
      if ($(this).scrollTop() > 20) {
        $('html').addClass('is-scroll');
      } else {
        $('html').removeClass('is-scroll');
      }
    });
  }
}

// 参考サイト
// https://hacknote.jp/archives/15289/

let cookie = {
	/**
	 * 取得
	 * @param {string} cookieName 取得するCookieの名前
	 */
	get : function(cookieName) {
		if (!navigator.cookieEnabled) return false;

		var getCookieAllData  = document.cookie,
		    getCookieAllDatas = getCookieAllData.split(';'),
		    getCookieDatas    = {},
		    cookieKeyValue    = [],
		    cookieKey         = '',
		    cookieValue       = '';

		var hashLength = function(arr) {
			var result = 0, key = false;

			if (typeof arr !== 'object') return false;

			for (key in arr) result++;

			return result;
		};

		for (var i = 0, len = getCookieAllDatas.length; i < len; i++) {
			if (getCookieAllDatas[i] && getCookieAllDatas[i] !== '') {
				cookieKeyValue = getCookieAllDatas[i].split('=');
				cookieKey      = cookieKeyValue[0].replace(/^\s+/, '');
				cookieValue    = '';

				if (cookieKeyValue[1]) {
					cookieValue = decodeURIComponent(cookieKeyValue[1]);
				}

				getCookieDatas[cookieKey] = cookieValue;
			}
		}

		if (hashLength(getCookieDatas) > 0) {
			if (cookieName && cookieName in getCookieDatas) {
				return getCookieDatas[cookieName];
			} else {
				return getCookieDatas;
			}
		} else {
			return '';
		}
	},

	/**
	 * 設定
	 * @param {string} cookieName 設定するCookieの名前
	 * @param {string} value 設定する値
	 * @param {string} domain 有効ドメイン
	 * @param {string} path 有効パス
	 * @param {string} expires 有効期限
	 * @param {boolean} secure セキュアとするか
	 * @return {boolean} 設定に成功した場合はtrue、失敗した場合はfalseを返す
	 */
	set : function(cookieName, value, domain, path, expires, secure) {
		if (!navigator.cookieEnabled || !cookieName || !value) return false;

		var date, dt, ts, ts_after;

		var setCookieData = cookieName + '=' + encodeURIComponent(value) + ';';

		if (domain) setCookieData += 'domain=' + domain + ';';

		if (path) setCookieData += 'path=' + path + ';';

		if (expires) {
      dt = new Date();
      ts = dt.getTime();

      // 1時間後
      ts_after = ts + (1000 * 60 * 60 * expires);

      date = new Date(ts_after);
			setCookieData += 'expires=' + date.toGMTString() + ';';
		}

		if (secure && secure === true && location.protocol.match(/https/)) {
			setCookieData += 'secure';
		}

		document.cookie = setCookieData;

		return true;
	},

	/**
	 * 削除
	 * @param {string} cookieName 削除するCookieの名前
	 * @return {boolean} 削除に成功した場合はtrue、失敗した場合はfalseを返す
	 */
	del : function(cookieName) {
		if (!navigator.cookieEnabled || !cookieName) return false;

		var getCookieDatas = this.get();

		if (getCookieDatas[cookieName]) {
			var setCookieData = cookieName + '=;',
			    date          = new Date(0);

			setCookieData += 'expires=' + date.toGMTString() + '';

			document.cookie = setCookieData;

			return true;
		}

		return false;
	}
};
class FaqAccordion {
  constructor(el,opt){
    this.el = el;
    if($("." + this.el).length > 0){
      this.event();
    }
  }
  event(){
    $("." + this.el).click(function(){
      $(this).next().slideToggle();
      $(this).find('.trigger').toggleClass('is-open');
    });
  }
}

class FormConvert {
  constructor(el,opt){
    this.el = el;
    if($("." + this.el).length > 0){
      this.event();
    }
  }
  event(){
    //全角から半角
    function replaceZtoH(str){
      str = str.replace(/[！-～]/g, function(s){
        return String.fromCharCode(s.charCodeAt(0) - 0xFEE0);
      });
      str = str.replace(/[-－﹣−‐⁃‑‒–—﹘―⎯⏤ーｰ─━]/g, '-');
      return str;
    }
    const el = document.querySelector('.js-convert');
    el.addEventListener('blur', () => {
      const br = el.value;
      const ar = replaceZtoH(br);
      el.value = ar;
    });
  }
}

// ------------------------------------------
// imgタグに「js-parallax」クラス、そのimgタグに要素をラップしてheightを指定すると、画像がはみ出た分が動く
// 「data-scrub」属性に数字を入れると、動きの滑らかさが変わる
// ------------------------------------------
class ImgParallax {
  constructor(el,opt){
    this.el = el;
    if($("." + this.el).length > 0){
      this.event();
    }
  }
  event(){
    let cls = this.el;
    let para_ele = document.querySelectorAll("." + this.el);
    para_ele.forEach((ele, index)=> {
      let property,scrub;

      $(ele).css({
        'object-fit':'cover',
        'object-position':'50% 0%',
        'width':'100%',
        'height':'100%',
      })

      property = '50% 100%';
      scrub = $(ele).data('scrub') ? $(ele).data('scrub') : 3;

      ele.classList.add(cls + index);
      gsap.to('.' + cls + index, {
        objectPosition: property,
        scrollTrigger: {
          trigger: '.' + cls + index, // 要素がビューポートに入ったときにアニメーション開始
          start: 'top bottom',
          end: 'bottom top',
          scrub: scrub,
          merker: true,
        }
      })
    })
  }
}

class InviewEvent {
  constructor(el, opt){
    $('.js-load-inview').animate({'opacity': '1'});
    this.el = "." + el;
    this.all = this.el + '-all';
    this.eachUp = this.el + '-each';
    this.eachItem = this.el + '-eachItem';
    this.eachItemFast = this.el + '-eachItem-fast';
    this.eachItemDelay = this.el + '-eachItem-delay';
    if($(this.el).length > 0){
      this.event();
    }
  }
  event() {
    let options = {
      threshold: [0, 0.05, 0.1, 1]
    }
    let optionsAll = {
      threshold: [0, 0.9, 1]
    }
    const observerEnter = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const item = entry.target;
        if(entry.intersectionRatio >= 0.05){
          item.classList.add('is-inview');
          if(item.classList.contains('js-inview-each')){
            this.inviewEachUp(item);
          }
          if(item.classList.contains('js-inview-lottie')){
            this.inviewLottie(item);
          }
          observerEnter.unobserve(item);
        }
      });
    }, options);
    const observerAll = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const item = entry.target;
        if(entry.intersectionRatio >= 0.9){
          item.classList.add('is-inview');
          if(item.classList.contains('js-inview-each')){
            this.inviewEachUp(item);
          }
          observerAll.unobserve(item);
        } else {
          item.classList.remove('is-inview');
        }
      });
    }, optionsAll);
    const inviewItem = document.querySelectorAll(this.el);
    const inviewAllItem = document.querySelectorAll(this.all);
    inviewItem.forEach((e)=>{
      observerEnter.observe(e);
    });
    inviewAllItem.forEach((e)=>{
      observerAll.observe(e);
    });
  }
  inviewEachUp(item){
    $(item).find(this.eachItem).each((i,el)=>{
      gsap.to($(el), {
        // duration: 0.8,
        // alpha: 1,
        delay: (0.2 * i),
        // y: 0,
        // x: 0,
        onComplete: ()=>{
          $(el).addClass('is-inview');
        }
      });
    });
    $(item).find(this.eachItemFast).each((i,el)=>{
      gsap.to($(el), {
        // duration: 0.8,
        // alpha: 1,
        delay: (0.1 * i),
        // y: 0,
        // x: 0,
        onComplete: ()=>{
          $(el).addClass('is-inview');
        }
      });
    });
    $(item).find(this.eachItemDelay).each((i,el)=>{
      gsap.to($(el), {
        // duration: 0.8,
        // alpha: 1,
        delay: (0.5 * i),
        // y: 0,
        // x: 0,
        onComplete: ()=>{
          $(el).addClass('is-inview');
        }
      });
    });
  }
  inviewLottie(item){
    if($(item).hasClass('js-lottie-flow')) {
      lottie.flow();
    }
    if($(item).hasClass('js-lottie-no01')) {
      lottie.no01();
    }
    if($(item).hasClass('js-lottie-no02')) {
      lottie.no02();
    }
    if($(item).hasClass('js-lottie-no03')) {
      lottie.no03();
    }
    if($(item).hasClass('js-lottie-no04')) {
      lottie.no04();
    }
    if($(item).hasClass('js-lottie-no05')) {
      lottie.no05();
    }
    if($(item).hasClass('js-lottie-no06')) {
      lottie.no06();
    }
    if($(item).hasClass('js-lottie-no07')) {
      lottie.no07();
    }
  }
}

// -------------------------------------------------
// スクロール禁止
// -------------------------------------------------
let scrollTop = 0;

function scrollBan() {
  const body = $('html')[0];
  if (body.classList.contains('is-hidden')) {
    // スクロール開放
    body.style.top = '';
    body.classList.remove('is-hidden');
    window.scrollTo(0, scrollTop);
  } else {
    // スクロール禁止
    scrollTop = window.scrollY;
    body.style.top = (scrollTop * -1) + 'px';
    body.classList.add('is-hidden');
  }
}
class SnsShare {
  constructor() {
    this.event();
  }

  event() {
    document.querySelectorAll('.js-sns-share').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();

        const url = btn.dataset.shareUrl;
        const title = btn.dataset.shareTitle;

        if (!url) {
          return;
        }

        if (navigator.share) {
          try {
            await navigator.share({ title, url });
          } catch (err) {
            if (err.name !== 'AbortError') {
              this.copyUrl(url);
            }
          }
          return;
        }

        this.copyUrl(url);
      });
    });
  }

  copyUrl(url) {
    if (!navigator.clipboard) {
      return;
    }

    navigator.clipboard.writeText(url).catch(() => {});
  }
}


(() => {
  let imgParallax = new ImgParallax('js-parallax');
  let inviewEvent = new InviewEvent('js-inview');
  let formConvert = new FormConvert('js-convert');
  let top = new Top('p-top');
  let common = new Common();
  let snsShare = new SnsShare();
  let faqAccordion = new FaqAccordion('js-faq-trigger');
  let works = new Works('js-works');
  let worksSingle = new WorksSingle('js-works-single');
  let recruit = new Recruit();
  let newsArchive = new NewsArchive();
  let columnArchive = new ColumnArchive();
  let contact = new Contact();
  let honbunStepFlow = new HonbunStepFlow();
})();

//DOMContentLoaded
$(() => {
  // ScrollHint
  document.querySelectorAll('.js-scrollhint').forEach((el) => {
    new ScrollHint(el, {
      suggestiveShadow: true,
      i18n: {
        scrollable: 'スクロールできます',
      },
    });
  });
});

//images resources loaded
$(window).on('load', () => {

  $(window).trigger('loading');
});

//after loading animation
$(window).on('loading', () => {
});